import React from "react";
import {
  Button,
  Col,
  Form,
  InputGroup,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";
import { SearchIcon } from "../../../../components/ui/icons";
import TreeView from "../../../../components/treeView";
import useCreateCap from "./useCreateCap";
import InformationCircle from "../../../../assets/images/info-circle.svg";
import { ICapEndpointRow } from "../capTypes";

interface IProps {
  handleShow: () => void;
  editEndpoint?: ICapEndpointRow | null;
}

const CreateCap: React.FC<IProps> = ({ handleShow, editEndpoint = null }) => {
  const {
    name,
    setName,
    templateId,
    isTemplateDivSelected,
    handleTemplateClick,
    handleGroupInputChange,
    groupSearchQuery,
    nodesToDisplay,
    handleDataFromTreeView,
    selectGroupState,
    setSelectGroupState,
    placedHolderGroupValues,
    handleSubmit,
    isSubmitting,
    selectedGroups,
    generatedWebhookUrl,
    showGeneratedModal,
    setShowGeneratedModal,
    templates,
    templateSearch,
    setTemplateSearch,
    isEditMode,
  } = useCreateCap({ editEndpoint, onCancel: handleShow });

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedWebhookUrl);
  };

  const onConfigure = async () => {
    await handleSubmit();
  };

  const handleGeneratedModalClose = () => {
    setShowGeneratedModal(false);
    handleShow();
  };

  return (
    <>
      <Row>
        <Col sm={12}>
          <div className="integration-body">
            <Col sm={6}>
              <div className="integration-input-content">
                <div className="integration-input">
                  <Form.Label>
                    {isEditMode ? "Edit Endpoint Name" : "Name"}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. NYU Rave Alert"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
              </div>
            </Col>

            <Col sm={6}>
              <div className="select-group-container">
                <div className="send-alerts-group-content">
                  <label className="heading">Send Alerts to Group(s)</label>
                  <div className="alerts-search-and-tree">
                    <div className="alert-search-input">
                      <InputGroup>
                        <Form.Control
                          placeholder={placedHolderGroupValues}
                          aria-label="Search"
                          aria-describedby="alert-search-input"
                          value={groupSearchQuery}
                          onChange={handleGroupInputChange}
                        />
                        <InputGroup.Text id="alert-search-input">
                          <SearchIcon />
                        </InputGroup.Text>
                      </InputGroup>
                    </div>
                    <div className="alert-checkbox">
                      <div className="select-all-checkbox">
                        <TreeView
                          nodes={nodesToDisplay}
                          onDataFromTreeView={handleDataFromTreeView}
                          isModalOrg={true}
                          isMultiSelect={true}
                          selectGroupState={selectGroupState}
                          setSelectGroupState={setSelectGroupState}
                          isSelectAllEnabled={true}
                          checkedBox={selectGroupState.checked}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            <Col sm={12}>
              <h3 className="choosetemplate_heading">
                {isEditMode ? "Edit Template" : "Choose Template"}
              </h3>
              <div className="cap-template-search">
                <InputGroup>
                  <Form.Control
                    placeholder="Search template"
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                  />
                  <InputGroup.Text>
                    <SearchIcon />
                  </InputGroup.Text>
                </InputGroup>
              </div>
              <div className="cap-template-grid">
                <Row className="gx-3 gy-4">
                  {(templates || []).map((template) => (
                    <Col
                      xs={12}
                      sm={6}
                      md={4}
                      key={template.templateId || template._id}
                    >
                      <div
                        className={`cap-template-card ${isTemplateDivSelected(template.templateId) ? "highlighted" : ""}`}
                        onClick={() =>
                          handleTemplateClick(template.templateId)
                        }
                      >
                        <div className="cap-template-image">
                          {template.imageUrl ? (
                            <img
                              src={template.imageUrl}
                              alt={template.name}
                            />
                          ) : (
                            <span>No image</span>
                          )}
                        </div>
                        <div className="cap-template-label">
                          <div
                            className="cap-template-name"
                            title={template.name}
                          >
                            {template.name}
                          </div>
                          {template.metadata?.firmwareVersion ? (
                            <OverlayTrigger
                              placement="right"
                              overlay={
                                <Tooltip id={`cap-template-${template.templateId}`}>
                                  Supported Firmware Version{" "}
                                  {template.metadata.firmwareVersion} and above.
                                </Tooltip>
                              }
                            >
                              <span className="cap-template-info">
                                <img
                                  src={InformationCircle}
                                  alt="Info"
                                  width={18}
                                  height={18}
                                />
                              </span>
                            </OverlayTrigger>
                          ) : null}
                        </div>
                      </div>
                    </Col>
                  ))}
                  {(templates || []).length === 0 && (
                    <div className="cap-template-empty">No templates found</div>
                  )}
                </Row>
              </div>
            </Col>

            <Col sm={12}>
              <div className="integration-footer">
                <Button
                  className="btn-style"
                  disabled={
                    !(
                      name.trim() &&
                      selectedGroups.length > 0 &&
                      templateId > 0
                    ) || isSubmitting
                  }
                  onClick={onConfigure}
                >
                  {isSubmitting
                    ? isEditMode
                      ? "Updating..."
                      : "Creating..."
                    : isEditMode
                      ? "Update Endpoint"
                      : "Create Endpoint"}
                </Button>
                <Button className="btn-cancel" onClick={handleShow}>
                  Cancel
                </Button>
              </div>
            </Col>
          </div>
        </Col>
      </Row>

      <Modal
        show={showGeneratedModal}
        onHide={handleGeneratedModalClose}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>CAP Endpoint Created</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Paste this URL into the CAP Channel / Web Services configuration of
            your CAP producer (e.g. Rave Alert). You can always copy it again
            from the endpoints list.
          </p>
          <InputGroup>
            <Form.Control value={generatedWebhookUrl} readOnly />
            <Button variant="outline-secondary" onClick={handleCopy}>
              Copy
            </Button>
          </InputGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="button-bg-blue"
            onClick={handleGeneratedModalClose}
          >
            Done
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateCap;
